import { useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AuthContext } from "../context/AuthContext";
import { WALLET_MOMENT_EVENT, takeWalletMoment, walletMomentPending } from "../lib/walletMoment";
import { waitForPushAskMoment } from "../lib/firstSignInMoment";

// Mounted once in App, inside the router (AuthContext sits above it): when
// the sign-in notification ask is over, a brand-new talent on an iPhone is
// taken to their QR page's Apple Wallet step (DJQrPage ?wallet=1). Android
// joins once Google Wallet leaves test mode (Google's publishing approval).
const WalletMoment: React.FC = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  // useNavigate() is a new function on every page change. Read it through a
  // ref: as an effect dependency, a tab tap during the wait below re-ran the
  // effect and cancelled the step (2026-10-10; DeepLinkHandler does the same).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const slug = user?.slug;
  const userId = user?._id;

  useEffect(() => {
    let cancelled = false;
    const go = () => {
      if (!slug || !userId || !Capacitor.isNativePlatform() || !walletMomentPending()) return;
      if (Capacitor.getPlatform() !== "ios") {
        takeWalletMoment(); // nothing to show yet on Android — don't keep the flag around
        return;
      }
      // Never on top of another ask: an install that already answered the
      // notification question fires this at once, while "Set up Face ID?"
      // may still be up — wait until that question is settled too. The flag
      // is only spent once the step really opens (a sign-out meanwhile keeps
      // it for the next sign-in).
      waitForPushAskMoment(userId, () => cancelled)
        .then(() => {
          if (takeWalletMoment()) navigateRef.current(`/qr/${slug}?wallet=1`);
        })
        .catch(() => {});
    };
    window.addEventListener(WALLET_MOMENT_EVENT, go);
    return () => {
      cancelled = true;
      window.removeEventListener(WALLET_MOMENT_EVENT, go);
    };
  }, [slug, userId]);

  return null;
};

export default WalletMoment;
